/**
 * Certificate & Document Types Configuration for School ERP
 * Comprehensive catalog of 57+ school certificate & document types grouped into 10 categories.
 */

export const CERTIFICATE_CATEGORIES = [
  { id: 'ACADEMIC', label: 'Academic Certificates', icon: 'GraduationCap' },
  { id: 'STUDENT_IDENTITY', label: 'Student Identity & Enrollment', icon: 'UserCheck' },
  { id: 'ATTENDANCE_DISCIPLINE', label: 'Attendance & Discipline', icon: 'CalendarCheck' },
  { id: 'ACHIEVEMENT', label: 'Academic Achievement & Merit', icon: 'Trophy' },
  { id: 'SPORTS_CULTURAL', label: 'Sports & Cultural Activities', icon: 'Activity' },
  { id: 'PARTICIPATION', label: 'Participation & Events', icon: 'Award' },
  { id: 'FEES', label: 'Fee & Financial Certificates', icon: 'Wallet' },
  { id: 'LEAVING_TRANSFER', label: 'Leaving & Transfer (TC)', icon: 'ArrowRightCircle' },
  { id: 'SPECIAL_DOCUMENTS', label: 'Special School Documents', icon: 'FileText' },
  { id: 'LETTERS', label: 'Official School Letters', icon: 'Mail' }
];

export const CERTIFICATE_TYPES = [
  // ==================== 1. ACADEMIC CERTIFICATES ====================
  {
    id: 'STUDY_CERTIFICATE',
    category: 'ACADEMIC',
    name: 'Study Certificate',
    codePrefix: 'STUDY',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'CLASSIC_SCHOOL',
    description: 'Certifies that the student is studying in a specific class and section during the academic year.',
    defaultContent: 'This is to certify that [STUDENT_NAME], Son/Daughter of [FATHER_NAME], bearing Admission No [ADMISSION_NO], is a bonafide student of this school studying in Class [CLASS_NAME] (Section [SECTION]) for the academic year [ACADEMIC_YEAR]. During this period, his/her conduct and character have been found to be good.'
  },
  {
    id: 'BONAFIDE_CERTIFICATE',
    category: 'ACADEMIC',
    name: 'Bonafide Certificate',
    codePrefix: 'BON',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'CLASSIC_SCHOOL',
    description: 'Official proof of regular student status at the school.',
    defaultContent: 'This is to certify that Master/Miss [STUDENT_NAME], Son/Daughter of [FATHER_NAME] and [MOTHER_NAME], residing at [STUDENT_ADDRESS], is a bonafide student of [SCHOOL_NAME], studying in Class [CLASS_NAME], Section [SECTION] during the academic year [ACADEMIC_YEAR].'
  },
  {
    id: 'CHARACTER_CERTIFICATE',
    category: 'ACADEMIC',
    name: 'Character Certificate',
    codePrefix: 'CHAR',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'TRADITIONAL_SCHOOL',
    description: 'Attests to the good moral character and behavior of the student during their schooling.',
    defaultContent: 'This is to certify that [STUDENT_NAME], Son/Daughter of [FATHER_NAME], bearing Admission No [ADMISSION_NO], has been a student of this school in Class [CLASS_NAME] during the academic year [ACADEMIC_YEAR]. To the best of our knowledge and belief, he/she bears an exemplary moral character and pleasant disposition.'
  },
  {
    id: 'CONDUCT_CERTIFICATE',
    category: 'ACADEMIC',
    name: 'Conduct Certificate',
    codePrefix: 'COND',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MINIMAL_OFFICIAL',
    description: 'Certifies student discipline, behavior, and conduct.',
    defaultContent: 'This is to certify that [STUDENT_NAME], Son/Daughter of [FATHER_NAME], a student of Class [CLASS_NAME] (Section [SECTION]) bearing Admission No [ADMISSION_NO], has maintained Good conduct and discipline during the academic year [ACADEMIC_YEAR].'
  },
  {
    id: 'COURSE_COMPLETION_CERTIFICATE',
    category: 'ACADEMIC',
    name: 'Course Completion Certificate',
    codePrefix: 'CCC',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'ELEGANT_ACADEMIC',
    description: 'Issued upon successful completion of a prescribed curriculum or syllabus.',
    defaultContent: 'This is to certify that [STUDENT_NAME], Admission No [ADMISSION_NO], has successfully completed the prescribed curriculum for Class [CLASS_NAME] at [SCHOOL_NAME] for the academic year [ACADEMIC_YEAR] with credit.'
  },
  {
    id: 'CLASS_COMPLETION_CERTIFICATE',
    category: 'ACADEMIC',
    name: 'Class Completion Certificate',
    codePrefix: 'CLS',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MODERN_PROFESSIONAL',
    description: 'Certifies that the student has fulfilled all class curriculum requirements.',
    defaultContent: 'This is to certify that [STUDENT_NAME], Son/Daughter of [FATHER_NAME], has successfully completed Class [CLASS_NAME] (Section [SECTION]) during the academic year [ACADEMIC_YEAR].'
  },
  {
    id: 'ACADEMIC_YEAR_COMPLETION_CERTIFICATE',
    category: 'ACADEMIC',
    name: 'Academic Year Completion Certificate',
    codePrefix: 'AYC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'CLASSIC_SCHOOL',
    description: 'Certifies completion of the entire academic session.',
    defaultContent: 'This is to certify that [STUDENT_NAME] has successfully completed the academic session [ACADEMIC_YEAR] in Class [CLASS_NAME] at [SCHOOL_NAME].'
  },
  {
    id: 'STUDENT_CLEARANCE_SLIP',
    category: 'ACADEMIC',
    name: 'All-Subject Clearance & Student No Dues Certificate',
    codePrefix: 'CND',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'COMPREHENSIVE_CLEARANCE',
    description: 'Comprehensive clearance slip covering all subjects, notes submission, attendance, library books, fee settlement, and subject teacher & parent signatures.',
    defaultContent: 'This is to certify that [STUDENT_NAME], Admission No: [ADMISSION_NO], Roll No: [ROLL_NO], studying in Class [CLASS_NAME] (Section [SECTION]) for academic session [ACADEMIC_YEAR], has completed all class notes, coursework, and cleared all departmental and fee obligations as verified below.'
  },
  {
    id: 'STUDY_AND_CONDUCT_CERTIFICATE',
    category: 'ACADEMIC',
    name: 'Study + Conduct Certificate',
    codePrefix: 'SCC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'CLASSIC_SCHOOL',
    description: 'Combined certificate confirming enrollment, class, and good conduct.',
    defaultContent: 'This is to certify that [STUDENT_NAME], Son/Daughter of [FATHER_NAME], is studying in Class [CLASS_NAME] (Section [SECTION]) during the academic year [ACADEMIC_YEAR]. His/Her character and conduct during the period of study have been Exemplary.'
  },
  {
    id: 'STUDY_AND_BONAFIDE_CERTIFICATE',
    category: 'ACADEMIC',
    name: 'Study + Bonafide Certificate',
    codePrefix: 'SBC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'CLASSIC_SCHOOL',
    description: 'Combined bonafide and study status confirmation.',
    defaultContent: 'This is to certify that [STUDENT_NAME], bearing Admission No [ADMISSION_NO], is a bonafide student of [SCHOOL_NAME] currently enrolled and studying in Class [CLASS_NAME] during [ACADEMIC_YEAR].'
  },
  {
    id: 'BONAFIDE_AND_CONDUCT_CERTIFICATE',
    category: 'ACADEMIC',
    name: 'Bonafide + Conduct Certificate',
    codePrefix: 'BCC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'ROYAL_GOLD',
    description: 'Combined bonafide enrollment and conduct certificate.',
    defaultContent: 'This is to certify that [STUDENT_NAME], Son/Daughter of [FATHER_NAME], is a bonafide student of Class [CLASS_NAME] at [SCHOOL_NAME] for the academic session [ACADEMIC_YEAR]. His/Her conduct and character have been consistently good.'
  },

  // ==================== 2. STUDENT IDENTITY & ENROLLMENT ====================
  {
    id: 'STUDENT_IDENTITY_CONFIRMATION',
    category: 'STUDENT_IDENTITY',
    name: 'Student Identity Confirmation Certificate',
    codePrefix: 'SIC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MINIMAL_OFFICIAL',
    description: 'Official confirmation of student personal particulars and identity in school records.',
    defaultContent: 'This is to confirm that [STUDENT_NAME], Son/Daughter of [FATHER_NAME] and [MOTHER_NAME], Date of Birth [DOB], is duly enrolled in our school records with Student ID [STUDENT_ID] and Admission No [ADMISSION_NO] in Class [CLASS_NAME].'
  },
  {
    id: 'STUDENT_STATUS_CERTIFICATE',
    category: 'STUDENT_IDENTITY',
    name: 'Student Status Certificate',
    codePrefix: 'SSC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MINIMAL_OFFICIAL',
    description: 'Certifies the active enrollment status of the student.',
    defaultContent: 'This certificate confirms that [STUDENT_NAME] is currently an active and regular student in Class [CLASS_NAME] at [SCHOOL_NAME] for the academic year [ACADEMIC_YEAR].'
  },
  {
    id: 'ENROLLMENT_CERTIFICATE',
    category: 'STUDENT_IDENTITY',
    name: 'Enrollment Certificate',
    codePrefix: 'ENR',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MODERN_PROFESSIONAL',
    description: 'Certifies official registration in the school roll register.',
    defaultContent: 'This is to certify that [STUDENT_NAME], bearing Admission No [ADMISSION_NO], was enrolled in [SCHOOL_NAME] on [DATE_OF_ADMISSION] and is presently registered in Class [CLASS_NAME].'
  },
  {
    id: 'ADMISSION_CONFIRMATION_CERTIFICATE',
    category: 'STUDENT_IDENTITY',
    name: 'Admission Confirmation Certificate',
    codePrefix: 'ACC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MODERN_PROFESSIONAL',
    description: 'Official letter/certificate confirming student admission into a specific class.',
    defaultContent: 'We are pleased to confirm that [STUDENT_NAME], Child of [FATHER_NAME], has been officially admitted to Class [CLASS_NAME] (Section [SECTION]) at [SCHOOL_NAME] for the academic year [ACADEMIC_YEAR].'
  },
  {
    id: 'REGULAR_STUDENT_CERTIFICATE',
    category: 'STUDENT_IDENTITY',
    name: 'Regular Student Certificate',
    codePrefix: 'RSC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'CLASSIC_SCHOOL',
    description: 'Attests that the student attends regular day-school classes.',
    defaultContent: 'This is to certify that [STUDENT_NAME] is a regular day-student attending Class [CLASS_NAME] at [SCHOOL_NAME] during the academic session [ACADEMIC_YEAR].'
  },
  {
    id: 'STUDENT_VERIFICATION_CERTIFICATE',
    category: 'STUDENT_IDENTITY',
    name: 'Student Verification Certificate',
    codePrefix: 'SVC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'GOVERNMENT_STYLE',
    description: 'Official verification document for banks, passport authorities, or external bodies.',
    defaultContent: 'Official Verification: This is to verify that [STUDENT_NAME], Son/Daughter of [FATHER_NAME], residing at [STUDENT_ADDRESS], is a bonafide student of [SCHOOL_NAME] studying in Class [CLASS_NAME] under Registration No [ADMISSION_NO].'
  },

  // ==================== 3. ATTENDANCE & DISCIPLINE ====================
  {
    id: 'GOOD_ATTENDANCE_CERTIFICATE',
    category: 'ATTENDANCE_DISCIPLINE',
    name: 'Good Attendance Certificate',
    codePrefix: 'ATT',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'GREEN_ACADEMIC',
    description: 'Recognizes exceptional school attendance record.',
    defaultContent: 'This Certificate of Good Attendance is awarded to [STUDENT_NAME] of Class [CLASS_NAME] (Section [SECTION]) in recognition of maintaining [ATTENDANCE_PERCENTAGE]% attendance during the academic year [ACADEMIC_YEAR].'
  },
  {
    id: 'ATTENDANCE_EXCELLENCE_CERTIFICATE',
    category: 'ATTENDANCE_DISCIPLINE',
    name: 'Attendance Excellence Certificate (100%)',
    codePrefix: 'ATTE',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'ACHIEVEMENT_GOLD',
    description: 'Honors perfect 100% or near-perfect attendance during the academic term.',
    defaultContent: 'This Certificate of Excellence is proudly presented to [STUDENT_NAME] of Class [CLASS_NAME] for maintaining an outstanding 100% attendance record throughout the academic year [ACADEMIC_YEAR].'
  },
  {
    id: 'SCHOOL_ATTENDANCE_CERTIFICATE',
    category: 'ATTENDANCE_DISCIPLINE',
    name: 'School Attendance Certificate',
    codePrefix: 'SAC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MINIMAL_OFFICIAL',
    description: 'Official statement of total working days and days attended.',
    defaultContent: 'This is to certify that [STUDENT_NAME], a student of Class [CLASS_NAME] (Section [SECTION]), has attended [DAYS_PRESENT] days out of total [TOTAL_WORKING_DAYS] working days ([ATTENDANCE_PERCENTAGE]%) during the academic year [ACADEMIC_YEAR].'
  },
  {
    id: 'DISCIPLINE_CERTIFICATE',
    category: 'ATTENDANCE_DISCIPLINE',
    name: 'Discipline Certificate',
    codePrefix: 'DISC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'CLASSIC_SCHOOL',
    description: 'Attests to adherence to school rules, punctuality, and discipline.',
    defaultContent: 'This is to certify that [STUDENT_NAME] of Class [CLASS_NAME] has consistently demonstrated high standards of discipline, punctuality, and respect for school regulations during the academic year [ACADEMIC_YEAR].'
  },
  {
    id: 'GOOD_CONDUCT_CERTIFICATE',
    category: 'ATTENDANCE_DISCIPLINE',
    name: 'Good Conduct Certificate',
    codePrefix: 'GCC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'TRADITIONAL_SCHOOL',
    description: 'Formal statement of exemplary conduct.',
    defaultContent: 'This is to certify that [STUDENT_NAME], Son/Daughter of [FATHER_NAME], has shown exemplary conduct, honesty, and cooperative behavior throughout his/her stay in [SCHOOL_NAME].'
  },
  {
    id: 'BEST_STUDENT_CERTIFICATE',
    category: 'ATTENDANCE_DISCIPLINE',
    name: 'Best Student Award Certificate',
    codePrefix: 'BEST',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'ROYAL_GOLD',
    description: 'Honors the student selected as Best Student of the Class/School.',
    defaultContent: 'This prestigious award is bestowed upon [STUDENT_NAME] of Class [CLASS_NAME] for outstanding academic excellence, leadership qualities, and exemplary character during the academic year [ACADEMIC_YEAR].'
  },

  // ==================== 4. ACADEMIC ACHIEVEMENT & MERIT ====================
  {
    id: 'MERIT_CERTIFICATE',
    category: 'ACHIEVEMENT',
    name: 'Merit Certificate',
    codePrefix: 'MER',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'ACHIEVEMENT_GOLD',
    description: 'Recognizes academic brilliance and top grades.',
    defaultContent: 'Certificate of Merit awarded to [STUDENT_NAME], Class [CLASS_NAME], Section [SECTION], for securing [RANK] position with [PERCENTAGE]% marks in the annual examinations for the academic session [ACADEMIC_YEAR].'
  },
  {
    id: 'RANK_CERTIFICATE',
    category: 'ACHIEVEMENT',
    name: 'Rank Certificate',
    codePrefix: 'RNK',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'ROYAL_GOLD',
    description: 'Certifies specific rank achieved in school or class tests.',
    defaultContent: 'This is to certify that [STUDENT_NAME], bearing Admission No [ADMISSION_NO], secured [RANK] Rank in Class [CLASS_NAME] in the Annual Assessment of [ACADEMIC_YEAR].'
  },
  {
    id: 'ACADEMIC_EXCELLENCE_CERTIFICATE',
    category: 'ACHIEVEMENT',
    name: 'Academic Excellence Certificate',
    codePrefix: 'AEC',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'ELEGANT_PURPLE',
    description: 'Honors superior performance across all academic subjects.',
    defaultContent: 'Certificate of Academic Excellence is proudly conferred upon [STUDENT_NAME] of Class [CLASS_NAME] for demonstrating exceptional scholastic aptitude and achieving Distinction during [ACADEMIC_YEAR].'
  },
  {
    id: 'SCHOLARSHIP_CERTIFICATE',
    category: 'ACHIEVEMENT',
    name: 'Scholarship Certificate',
    codePrefix: 'SCH',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'PREMIUM_BORDER',
    description: 'Certifies award of school or state merit scholarship.',
    defaultContent: 'This is to certify that [STUDENT_NAME], Class [CLASS_NAME], has been awarded the [AWARD_NAME] for outstanding academic and co-curricular merit for the academic year [ACADEMIC_YEAR].'
  },
  {
    id: 'EXCELLENCE_CERTIFICATE',
    category: 'ACHIEVEMENT',
    name: 'Certificate of Excellence',
    codePrefix: 'EXC',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'ACHIEVEMENT_GOLD',
    description: 'General certificate of excellence for outstanding student accomplishment.',
    defaultContent: 'In recognition of outstanding dedication, perseverance, and high scholastic achievement, this Certificate of Excellence is awarded to [STUDENT_NAME] of Class [CLASS_NAME].'
  },
  {
    id: 'OUTSTANDING_PERFORMANCE_CERTIFICATE',
    category: 'ACHIEVEMENT',
    name: 'Outstanding Performance Certificate',
    codePrefix: 'OPC',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'ROYAL_GOLD',
    description: 'Honors exceptional performance in specific subjects or projects.',
    defaultContent: 'This Certificate is presented to [STUDENT_NAME] of Class [CLASS_NAME] in honor of outstanding performance in [SUBJECTS] during the academic year [ACADEMIC_YEAR].'
  },

  // ==================== 5. SPORTS & CULTURAL ACTIVITIES ====================
  {
    id: 'SPORTS_ACHIEVEMENT_CERTIFICATE',
    category: 'SPORTS_CULTURAL',
    name: 'Sports Achievement Certificate',
    codePrefix: 'SPT',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'SPORTS_CERTIFICATE',
    description: 'Celebrates athletic victories, tournament wins, and medals.',
    defaultContent: 'This Certificate of Sports Achievement is awarded to [STUDENT_NAME] of Class [CLASS_NAME] for securing [POSITION] place in [EVENT_NAME] held on [EVENT_DATE] at [SCHOOL_NAME].'
  },
  {
    id: 'CULTURAL_ACHIEVEMENT_CERTIFICATE',
    category: 'SPORTS_CULTURAL',
    name: 'Cultural Achievement Certificate',
    codePrefix: 'CUL',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'CULTURAL_CERTIFICATE',
    description: 'Honors excellence in music, dance, drama, art, or literary events.',
    defaultContent: 'This Certificate is proudly presented to [STUDENT_NAME] of Class [CLASS_NAME] for winning [POSITION] prize in [EVENT_NAME] during the Annual Cultural Fest [ACADEMIC_YEAR].'
  },
  {
    id: 'COMPETITION_WINNER_CERTIFICATE',
    category: 'SPORTS_CULTURAL',
    name: 'Competition Winner Certificate',
    codePrefix: 'WIN',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'ACHIEVEMENT_GOLD',
    description: 'Awarded to winners of school, inter-school, or zonal competitions.',
    defaultContent: 'This is to certify that [STUDENT_NAME] of Class [CLASS_NAME] has won [POSITION] position in the [COMPETITION_NAME] held at [SCHOOL_NAME] on [EVENT_DATE].'
  },
  {
    id: 'CO_CURRICULAR_ACHIEVEMENT',
    category: 'SPORTS_CULTURAL',
    name: 'Co-curricular Achievement Certificate',
    codePrefix: 'CCA',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'MODERN_BLUE',
    description: 'Honors active participation and awards in co-curricular clubs.',
    defaultContent: 'Certificate of Co-curricular Achievement presented to [STUDENT_NAME] of Class [CLASS_NAME] for outstanding contribution and performance in [ACTIVITY_NAME] during [ACADEMIC_YEAR].'
  },

  // ==================== 6. PARTICIPATION & EVENTS ====================
  {
    id: 'PARTICIPATION_CERTIFICATE',
    category: 'PARTICIPATION',
    name: 'Certificate of Participation',
    codePrefix: 'PAR',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'MODERN_BLUE',
    description: 'Appreciation certificate for taking part in events and exhibitions.',
    defaultContent: 'This is to certify that [STUDENT_NAME] of Class [CLASS_NAME] actively participated in [EVENT_NAME] organized by [SCHOOL_NAME] on [EVENT_DATE].'
  },
  {
    id: 'COMPETITION_PARTICIPATION',
    category: 'PARTICIPATION',
    name: 'Competition Participation Certificate',
    codePrefix: 'CPC',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'MODERN_BLUE',
    description: 'Issued to contestants participating in academic or skill contests.',
    defaultContent: 'This Certificate of Participation is awarded to [STUDENT_NAME] for enthusiastic participation in the [COMPETITION_NAME] held on [EVENT_DATE].'
  },
  {
    id: 'WORKSHOP_PARTICIPATION',
    category: 'PARTICIPATION',
    name: 'Workshop Participation Certificate',
    codePrefix: 'WPC',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'MINIMAL_PROFESSIONAL',
    description: 'Certifies attendance at student skill development workshops.',
    defaultContent: 'This is to certify that [STUDENT_NAME] of Class [CLASS_NAME] attended the workshop on [EVENT_NAME] conducted at [SCHOOL_NAME] on [EVENT_DATE].'
  },
  {
    id: 'SEMINAR_PARTICIPATION',
    category: 'PARTICIPATION',
    name: 'Seminar Participation Certificate',
    codePrefix: 'SPC',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'MINIMAL_PROFESSIONAL',
    description: 'Certifies attendance and participation in school seminars.',
    defaultContent: 'This Certificate verifies that [STUDENT_NAME] participated in the seminar on [EVENT_NAME] held at [SCHOOL_NAME] on [EVENT_DATE].'
  },
  {
    id: 'CLUB_PARTICIPATION',
    category: 'PARTICIPATION',
    name: 'Club / Society Participation Certificate',
    codePrefix: 'CLUB',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'GREEN_ACADEMIC',
    description: 'Certifies active membership in Science, Eco, Literary, or Robotics Club.',
    defaultContent: 'This is to certify that [STUDENT_NAME] has been an active and valued member of the [ACTIVITY_NAME] Club at [SCHOOL_NAME] during the academic session [ACADEMIC_YEAR].'
  },
  {
    id: 'SCHOOL_FUNCTION_PARTICIPATION',
    category: 'PARTICIPATION',
    name: 'School Function Participation Certificate',
    codePrefix: 'SFP',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'CULTURAL_CERTIFICATE',
    description: 'Appreciation for volunteering or performing in Annual Day, Sports Day, or exhibitions.',
    defaultContent: 'This Certificate of Appreciation is awarded to [STUDENT_NAME] of Class [CLASS_NAME] for outstanding contribution and performance during [EVENT_NAME] in [ACADEMIC_YEAR].'
  },
  {
    id: 'APPRECIATION_CERTIFICATE',
    category: 'PARTICIPATION',
    name: 'Certificate of Appreciation',
    codePrefix: 'APP',
    defaultOrientation: 'LANDSCAPE_AWARD',
    defaultTheme: 'ROYAL_GOLD',
    description: 'Formal appreciation for student service, help, or leadership.',
    defaultContent: 'This Certificate of Appreciation is warmly presented to [STUDENT_NAME] in grateful recognition of valuable service, teamwork, and exemplary commitment to [SCHOOL_NAME].'
  },

  // ==================== 7. FEE & FINANCIAL CERTIFICATES ====================
  {
    id: 'FEE_PAYMENT_CERTIFICATE',
    category: 'FEES',
    name: 'Fee Payment Certificate',
    codePrefix: 'FEE',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MINIMAL_OFFICIAL',
    description: 'Official certificate stating school fees paid for income tax / reimbursement claims.',
    defaultContent: 'This is to certify that the parents/guardians of [STUDENT_NAME], studying in Class [CLASS_NAME] (Section [SECTION]), have paid a total fee of Rs. [PAID_AMOUNT] towards Tuition and School Fees for the academic year [ACADEMIC_YEAR].'
  },
  {
    id: 'NO_DUE_CERTIFICATE',
    category: 'FEES',
    name: 'No Due Certificate',
    codePrefix: 'NODUE',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MINIMAL_OFFICIAL',
    description: 'Confirms clearance of all dues across Tuition, Library, Transport, and Sports.',
    defaultContent: 'This is to certify that there are No Dues pending against [STUDENT_NAME], Admission No [ADMISSION_NO], Class [CLASS_NAME]. All school tuition, library books, laboratory materials, and transport charges have been fully cleared up to [ISSUE_DATE].'
  },
  {
    id: 'FEE_CLEARANCE_CERTIFICATE',
    category: 'FEES',
    name: 'Fee Clearance Certificate',
    codePrefix: 'FCC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MINIMAL_OFFICIAL',
    description: 'Statement confirming zero balance on school ledger.',
    defaultContent: 'Official Statement: [STUDENT_NAME], Son/Daughter of [FATHER_NAME], enrolled in Class [CLASS_NAME], has cleared all financial obligations with [SCHOOL_NAME] for the session [ACADEMIC_YEAR]. Outstanding balance: Rs. 0.00.'
  },
  {
    id: 'COMPREHENSIVE_NO_DUES_CERTIFICATE',
    category: 'FEES',
    name: 'All-Subject & Comprehensive No Dues Certificate',
    codePrefix: 'CND',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'COMPREHENSIVE_CLEARANCE',
    description: 'Comprehensive clearance slip covering all subjects, notes submission, attendance, library books, fee settlement, and subject teacher & parent signatures.',
    defaultContent: 'This is to certify that [STUDENT_NAME], Admission No: [ADMISSION_NO], Roll No: [ROLL_NO], studying in Class [CLASS_NAME] (Section [SECTION]) for academic session [ACADEMIC_YEAR], has completed all class notes, coursework, and cleared all departmental and fee obligations as verified below.'
  },

  // ==================== 8. LEAVING & TRANSFER (TC) ====================
  {
    id: 'TRANSFER_CERTIFICATE',
    category: 'LEAVING_TRANSFER',
    name: 'Transfer Certificate (TC)',
    codePrefix: 'TC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'DEDICATED_TC',
    isDedicatedTC: true,
    description: 'Statutory Board/Government compliant school transfer certificate with 20+ structured items.',
    defaultContent: 'TRANSFER CERTIFICATE\n\n1. Name of Pupil: [STUDENT_NAME]\n2. Father\'s Name: [FATHER_NAME]\n3. Mother\'s Name: [MOTHER_NAME]\n4. Date of Birth: [DOB]\n5. Date of Admission: [DATE_OF_ADMISSION]\n6. Class in which student last studied: [CLASS_NAME]\n7. School/Board Annual Examination last taken: [CLASS_NAME] - Promoted\n8. Whether qualified for promotion: [PROMOTION_STATUS]\n9. Month up to which school dues paid: Fully Paid\n10. Total working days: [TOTAL_WORKING_DAYS], Days present: [DAYS_PRESENT]\n11. General Conduct: [CONDUCT]\n12. Reason for leaving: [REASON_FOR_LEAVING]\n13. Date of Application: [DATE_OF_LEAVING]\n14. Date of Issue: [ISSUE_DATE]'
  },
  {
    id: 'SCHOOL_LEAVING_CERTIFICATE',
    category: 'LEAVING_TRANSFER',
    name: 'School Leaving Certificate (SLC)',
    codePrefix: 'SLC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'DEDICATED_TC',
    isDedicatedTC: true,
    description: 'Formal school leaving certificate upon graduation or school change.',
    defaultContent: 'This is to certify that [STUDENT_NAME], bearing Admission No [ADMISSION_NO], attended [SCHOOL_NAME] from [DATE_OF_ADMISSION] to [DATE_OF_LEAVING]. He/She has completed his/her schooling up to Class [CLASS_NAME] with a good moral character.'
  },
  {
    id: 'MIGRATION_CERTIFICATE',
    category: 'LEAVING_TRANSFER',
    name: 'Student Migration Certificate',
    codePrefix: 'MIG',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'GOVERNMENT_STYLE',
    description: 'Issued when student transfers to another state, board, or schooling authority.',
    defaultContent: 'This institution has no objection to [STUDENT_NAME], Son/Daughter of [FATHER_NAME], Class [CLASS_NAME], migrating to any other recognized School or Educational Board.'
  },

  // ==================== 9. SPECIAL SCHOOL DOCUMENTS ====================
  {
    id: 'DATE_OF_BIRTH_CERTIFICATE',
    category: 'SPECIAL_DOCUMENTS',
    name: 'Date of Birth Certificate',
    codePrefix: 'DOB',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'CLASSIC_SCHOOL',
    description: 'Certifies the date of birth of the student as per official school admission register.',
    defaultContent: 'As per the School Admission Register of [SCHOOL_NAME], the Date of Birth of [STUDENT_NAME], Child of [FATHER_NAME] and [MOTHER_NAME], is [DOB].'
  },
  {
    id: 'MEDIUM_OF_INSTRUCTION_CERTIFICATE',
    category: 'SPECIAL_DOCUMENTS',
    name: 'Medium of Instruction Certificate',
    codePrefix: 'MOI',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MODERN_PROFESSIONAL',
    description: 'Certifies that the language of instruction and examination in the school is English.',
    defaultContent: 'This is to certify that [STUDENT_NAME], a student of Class [CLASS_NAME] at [SCHOOL_NAME], has pursued his/her entire school education in ENGLISH medium.'
  },
  {
    id: 'ENGLISH_MEDIUM_CERTIFICATE',
    category: 'SPECIAL_DOCUMENTS',
    name: 'English Medium Certificate',
    codePrefix: 'EMC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MODERN_PROFESSIONAL',
    description: 'Certifies schooling was completed in an English-medium environment.',
    defaultContent: 'This is to certify that [SCHOOL_NAME] is a recognized English Medium institution, and [STUDENT_NAME] has studied all subjects (except language options) in English medium.'
  },
  {
    id: 'PARENT_GUARDIAN_CERTIFICATE',
    category: 'SPECIAL_DOCUMENTS',
    name: 'Parent / Guardian Confirmation Certificate',
    codePrefix: 'PGC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MINIMAL_OFFICIAL',
    description: 'Confirms parentage and guardian contact details for official purposes.',
    defaultContent: 'This is to confirm that according to our admission records, [FATHER_NAME] and [MOTHER_NAME] are the recorded parents/guardians of [STUDENT_NAME], currently studying in Class [CLASS_NAME].'
  },
  {
    id: 'ADDRESS_VERIFICATION_CERTIFICATE',
    category: 'SPECIAL_DOCUMENTS',
    name: 'Address Verification Certificate',
    codePrefix: 'AVC',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'MINIMAL_OFFICIAL',
    description: 'Verifies residential address as submitted during school admission.',
    defaultContent: 'This is to certify that [STUDENT_NAME], a student of Class [CLASS_NAME], resides at [STUDENT_ADDRESS] as per the official residential records maintained at [SCHOOL_NAME].'
  },

  // ==================== 10. OFFICIAL SCHOOL LETTERS ====================
  {
    id: 'SCHOOL_VERIFICATION_LETTER',
    category: 'LETTERS',
    name: 'School Verification Letter',
    codePrefix: 'SVL',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'FORMAL_LETTER',
    description: 'Formal letter verifying student credentials to external organizations.',
    defaultContent: 'To Whom It May Concern:\n\nThis letter is to formally verify that [STUDENT_NAME] is a bonafide student of [SCHOOL_NAME], currently enrolled in Class [CLASS_NAME], Section [SECTION] for the academic year [ACADEMIC_YEAR]. All academic and personal records on file are genuine and verified.'
  },
  {
    id: 'RECOMMENDATION_LETTER',
    category: 'LETTERS',
    name: 'Recommendation Letter',
    codePrefix: 'LOR',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'FORMAL_LETTER',
    description: 'Letter of recommendation highlighting academic talent, character, and achievements.',
    defaultContent: 'To Whom It May Concern:\n\nIt is my privilege to recommend [STUDENT_NAME], a student of Class [CLASS_NAME] at [SCHOOL_NAME]. During his/her schooling, he/she has displayed exceptional academic curiosity, discipline, and strong moral principles. I strongly recommend him/her for future academic endeavors.'
  },
  {
    id: 'REFERENCE_LETTER',
    category: 'LETTERS',
    name: 'Reference Letter',
    codePrefix: 'REF',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'FORMAL_LETTER',
    description: 'Formal school reference letter from the Principal / Headmaster.',
    defaultContent: 'To Whom It May Concern:\n\nI am pleased to provide this official academic and character reference for [STUDENT_NAME], who has been a student in Class [CLASS_NAME] at [SCHOOL_NAME]. He/She has maintained high ethical and educational standards throughout.'
  },
  {
    id: 'TRANSFER_RECOMMENDATION_LETTER',
    category: 'LETTERS',
    name: 'Transfer Recommendation Letter',
    codePrefix: 'TRL',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'FORMAL_LETTER',
    description: 'Recommends transferring student to a receiving school due to relocation.',
    defaultContent: 'To The Principal,\n[Receiving School Name]\n\nRespected Sir/Madam,\n\nWe warmly recommend [STUDENT_NAME], studying in Class [CLASS_NAME] at our school, for admission to your esteemed institution. Due to [REASON_FOR_LEAVING], the student is relocating. He/She has been an upright, hardworking, and responsible pupil.'
  },
  {
    id: 'ADMISSION_OFFER_LETTER',
    category: 'LETTERS',
    name: 'Admission Offer Letter',
    codePrefix: 'AOL',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'FORMAL_LETTER',
    description: 'Formal school admission offer and welcome letter to parents.',
    defaultContent: 'Dear Parent/Guardian of [STUDENT_NAME],\n\nWe are delighted to offer admission to your ward [STUDENT_NAME] into Class [CLASS_NAME] (Section [SECTION]) at [SCHOOL_NAME] for the academic session [ACADEMIC_YEAR]. We look forward to partnering with you in providing a rich learning journey.'
  },
  {
    id: 'SCHOOL_JOINING_LETTER',
    category: 'LETTERS',
    name: 'School Joining Letter',
    codePrefix: 'SJL',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'FORMAL_LETTER',
    description: 'Confirms that the student has joined the school on a specific date.',
    defaultContent: 'This is to certify that [STUDENT_NAME] has reported and joined regular classes in Class [CLASS_NAME] at [SCHOOL_NAME] with effect from [DATE_OF_ADMISSION].'
  },
  {
    id: 'CUSTOM_SCHOOL_LETTER',
    category: 'LETTERS',
    name: 'Custom School Letter',
    codePrefix: 'CSL',
    defaultOrientation: 'PORTRAIT',
    defaultTheme: 'FORMAL_LETTER',
    description: 'Customizable blank school letterhead template.',
    defaultContent: 'To Whom It May Concern:\n\nThis is to state in respect of [STUDENT_NAME], Son/Daughter of [FATHER_NAME], Class [CLASS_NAME], that [Type your custom letter text here...]'
  }
];

export const PLACEHOLDERS_DICTIONARY = [
  {
    group: 'Student Particulars',
    tokens: [
      { token: '[STUDENT_NAME]', label: 'Student Name', sample: 'Rahul Kumar' },
      { token: '[FATHER_NAME]', label: "Father's Name", sample: 'Suresh Kumar' },
      { token: '[MOTHER_NAME]', label: "Mother's Name", sample: 'Sunita Devi' },
      { token: '[GUARDIAN_NAME]', label: 'Guardian Name', sample: 'Suresh Kumar' },
      { token: '[DOB]', label: 'Date of Birth', sample: '15/08/2012' },
      { token: '[GENDER]', label: 'Gender', sample: 'Male' },
      { token: '[STUDENT_ID]', label: 'Student ID', sample: 'STU-2026-0042' },
      { token: '[ADMISSION_NO]', label: 'Admission No', sample: 'ADM-8921' },
      { token: '[ROLL_NO]', label: 'Roll Number', sample: '14' },
      { token: '[STUDENT_ADDRESS]', label: 'Residential Address', sample: '#42, 3rd Cross, MG Road, Bengaluru' }
    ]
  },
  {
    group: 'Academic Details',
    tokens: [
      { token: '[CLASS_NAME]', label: 'Class', sample: 'Class 10' },
      { token: '[SECTION]', label: 'Section', sample: 'A' },
      { token: '[ACADEMIC_YEAR]', label: 'Academic Year', sample: '2026-27' },
      { token: '[DATE_OF_ADMISSION]', label: 'Admission Date', sample: '01/06/2020' },
      { token: '[PREVIOUS_CLASS]', label: 'Previous Class', sample: 'Class 9' },
      { token: '[NEXT_CLASS]', label: 'Next Class', sample: 'Class 11' },
      { token: '[SUBJECTS]', label: 'Subjects Studied', sample: 'English, Mathematics, Science, Social Studies, Hindi' }
    ]
  },
  {
    group: 'School Branding & Information',
    tokens: [
      { token: '[SCHOOL_NAME]', label: 'School Name', sample: 'Delhi Public School' },
      { token: '[SCHOOL_ADDRESS]', label: 'School Address', sample: 'Sector 4, Main Campus, City' },
      { token: '[SCHOOL_PHONE]', label: 'School Phone', sample: '+91 9876543210' },
      { token: '[SCHOOL_EMAIL]', label: 'School Email', sample: 'info@school.edu' },
      { token: '[SCHOOL_WEBSITE]', label: 'School Website', sample: 'www.school.edu' },
      { token: '[SCHOOL_REGISTRATION_NO]', label: 'Registration / Affiliation No', sample: 'CBSE/AFF/2026/894' },
      { token: '[UDISE_CODE]', label: 'UDISE Code', sample: '29280401823' },
      { token: '[PRINCIPAL_NAME]', label: 'Principal Name', sample: 'Dr. S. Sharma' },
      { token: '[HEADMASTER_NAME]', label: 'Headmaster Name', sample: 'Mr. R. V. Patel' },
      { token: '[CLASS_TEACHER_NAME]', label: 'Class Teacher Name', sample: 'Mrs. Ananya Roy' }
    ]
  },
  {
    group: 'Certificate & Issuance',
    tokens: [
      { token: '[CERTIFICATE_NO]', label: 'Certificate Number', sample: 'STUDY/2026/000001' },
      { token: '[ISSUE_DATE]', label: 'Issue Date', sample: '26/08/2026' },
      { token: '[ISSUE_PLACE]', label: 'Issue Place', sample: 'Bengaluru' }
    ]
  },
  {
    group: 'Attendance & Performance',
    tokens: [
      { token: '[ATTENDANCE_PERCENTAGE]', label: 'Attendance %', sample: '96.5' },
      { token: '[TOTAL_WORKING_DAYS]', label: 'Total Working Days', sample: '220' },
      { token: '[DAYS_PRESENT]', label: 'Days Present', sample: '212' },
      { token: '[PERCENTAGE]', label: 'Marks %', sample: '94.2' },
      { token: '[GRADE]', label: 'Grade', sample: 'A+' },
      { token: '[RANK]', label: 'Rank', sample: '1st' }
    ]
  },
  {
    group: 'Transfer & Leaving (TC Specific)',
    tokens: [
      { token: '[REASON_FOR_LEAVING]', label: 'Reason for Leaving', sample: 'Parent Transfer / Relocation' },
      { token: '[PROMOTION_STATUS]', label: 'Promotion Status', sample: 'Promoted to Class 11' },
      { token: '[CONDUCT]', label: 'General Conduct', sample: 'Exemplary' },
      { token: '[DATE_OF_LEAVING]', label: 'Date of Leaving', sample: '31/03/2026' }
    ]
  },
  {
    group: 'Achievements & Events',
    tokens: [
      { token: '[EVENT_NAME]', label: 'Event Name', sample: 'Inter-School Science Olympiad' },
      { token: '[COMPETITION_NAME]', label: 'Competition Name', sample: 'State Level Debate Championship' },
      { token: '[POSITION]', label: 'Position / Prize', sample: '1st Prize / Gold Medal' },
      { token: '[AWARD_NAME]', label: 'Award Name', sample: 'Governor Merit Trophy' },
      { token: '[EVENT_DATE]', label: 'Event Date', sample: '12/08/2026' },
      { token: '[ACTIVITY_NAME]', label: 'Activity Name', sample: 'Robotics & STEM Club' }
    ]
  },
  {
    group: 'Fees & Accounts',
    tokens: [
      { token: '[TOTAL_FEE]', label: 'Total Fee', sample: '45,000' },
      { token: '[PAID_AMOUNT]', label: 'Paid Amount', sample: '45,000' },
      { token: '[OUTSTANDING_AMOUNT]', label: 'Outstanding Amount', sample: '0' },
      { token: '[FEE_STATUS]', label: 'Fee Status', sample: 'Fully Paid / No Due' }
    ]
  }
];
